import * as React from "react"
import { graphql, useStaticQuery } from "gatsby"
import styled from "@emotion/styled"
import Layout from "components/layout"
import Container from "components/container"
import { H3, P } from "components/headings"
import SEO from "components/SEO"
import ModuleRender from "components/ModuleRender"
import { HeaderConfig } from "components/Header"
import { HeaderVariant } from "components/Header/types"
import { BreakPoints, mediaBreakpoint } from "settings/breakpoints"
import { css } from "@emotion/react"
import DoubleBarChart from "components/DemocracyPerceptionIndex/DoubleBarChart"
import Media from "components/Media"
import BarChart from "../components/DemocracyPerceptionIndex/BarChart"
import Button from "../components/button"
import CoverBackground from "../images/CTA-Background.svg"

const query = graphql`
	query {
		contentfulPage(slug: { eq: "democracy-perception-index-report-2021" }) {
			headerVariant
			seo {
				...SEO
			}
			sections {
				... on ContentfulAlternatingCards {
					...AlternatingCards
				}
				... on ContentfulBrandImagesSection {
					...BrandImages
				}
				... on ContentfulBrandImagesWithReviews {
					...BrandImagesWithReviews
				}
				... on ContentfulLandingSections {
					...Section
				}
				... on ContentfulReferencesSection {
					...ReferencesSection
				}
			}
		}
		dpiMapImage: contentfulAsset(title: { eq: "dpi_map" }) {
			...MediaOriginal
		}
		dpiMapIndicator: contentfulAsset(title: { eq: "Indicator" }) {
			...MediaOriginal
		}
		dpiRaportImage: contentfulAsset(title: { eq: "DPI Raport Image" }) {
			...MediaOriginal
		}
	}
`

const VideoFormContainer = styled.section`
	display: flex;
	flex-direction: column;
	padding: 60px 0;

	${mediaBreakpoint(BreakPoints.LG)} {
		padding: 84px 0;
	}
`

const VideoFormTop = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;

	${mediaBreakpoint(BreakPoints.LG)} {
		flex-direction: row;
	}
`

const VideoFormLeft = styled.div`
	height: 100%;
	width: 100%;
	margin-bottom: 20px;
	white-space: pre-line;

	${mediaBreakpoint(BreakPoints.LG)} {
		padding-right: 130px;
		width: 60%;
	}
`

const MainTitle = styled.h1`
	font-size: 4.8rem;
	line-height: 5.8rem;
	font-weight: 800;
`

export const Title = styled.h2`
	font-size: 4rem;
	line-height: 5rem;
	font-weight: 800;
	margin-bottom: 20px;
`

const VideoFormRight = styled.div`
	height: 100%;
	width: 100%;

	[data-element="cover"] {
		padding: 0;
	}

	${mediaBreakpoint(BreakPoints.LG)} {
		width: 40%;
	}
`

const RaportImage = styled.div`
	height: 100%;
	width: auto;
	max-width: 100%;
`

const ChartsContainer = styled.section`
	padding: 80px 0;
	background-color: #30125c;
	background: linear-gradient(225deg, #30125c, #170239);

	&.whiteBg {
		background: none;
		background-color: #fff;
	}
`

const ChartsTop = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	text-align: center;
	margin-bottom: 40px;
`

const PieChartTitle = styled.h2`
	font-size: 2rem;
	line-height: 2.8rem;
	font-weight: 800;
	margin-bottom: 40px;

	${mediaBreakpoint(BreakPoints.MD)} {
		font-size: 4rem;
		line-height: 5rem;
	}
`

const ChartsBottom = styled.div`
	display: flex;
	flex-direction: column;

	${mediaBreakpoint(BreakPoints.MD)} {
		flex-direction: row;
	}
`

const ChartsLeft = styled.div`
	display: flex;
	flex-direction: column;

	${mediaBreakpoint(BreakPoints.MD)} {
		padding-right: 55px;
		width: 50%;
	}

	${mediaBreakpoint(BreakPoints.LG)} {
		p {
			white-space: pre;
		}
	}
`

const ChartsRight = styled.div`
	span {
		font-size: 1rem;
		line-height: 2.8rem;
		font-weight: 400;
		opacity: 0.7;
	}

	${mediaBreakpoint(BreakPoints.MD)} {
		width: 50%;
		padding-left: 80px;
	}
`

const whiteText = css`
	color: #fff;
`

const footerCoverStyles = css`
	display: flex;
	align-items: flex-start;
	min-height: 496px;
	background: url("${CoverBackground}") no-repeat center top var(--main-color);
	background-size: cover;
	padding: 75px 0 152px;
	min-height: 552px;
	${mediaBreakpoint(BreakPoints.SM)} {
		min-height: 496px;
		align-items: center;
		padding: 116px 0 80px;
	}
`

const coverHeadingStyles = css`
	color: #fff;
	text-align: center;
	margin: 0 0 24px;
`
const coverDescriptionStyles = css`
	width: 432px;
	max-width: 100%;
	text-align: center;
	color: #fff;
	margin: 0 0 42px;
`
const coverContainerStyles = css`
	display: flex;
	flex-direction: column;
	align-items: center;
`

export const Title2 = styled.h1`
	font-size: 2.4rem;
	line-height: 2.4rem;
`

export const SubTitle2 = styled.h3`
	font-size: 1.2rem;
	line-height: 1.2rem;
	font-weight: 600;
`

const ChartsSecondary = styled.div`
	text-align: left;
	width: 100%;
	margin-top: 30px;
`

const Map = styled.div`
	width: 100%;

	.gatsby-image-wrapper {
		width: 100% !important;
	}
`

const SubMap = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;
`

const DemocracyDownload = () => {
	const { contentfulPage, dpiMapImage, dpiMapIndicator, dpiRaportImage } = useStaticQuery(query)
	const { sections, seo, headerVariant } = contentfulPage

	const headerConfig: HeaderConfig = React.useMemo(() => {
		return {
			variant: headerVariant || HeaderVariant.Default,
		}
	}, [headerVariant])

	const barCharts2 = [
		{
			label: "Global",
			color: "#01C1B7",
			value: 58,
		},
		{
			label: "",
			color: "#fff",
			value: 0,
		},
		{
			label: "Free",
			color: "#01C1B7",
			value: 51,
		},
		{
			label: "Less Free",
			color: "#01C1B7",
			value: 65,
		},
		{
			label: "",
			color: "#fff",
			value: 0,
		},
		{
			label: "Asia",
			color: "#01C1B7",
			value: 75,
		},
		{
			label: "Europe",
			color: "#E2154A",
			value: 45,
		},
		{
			label: "Latin America",
			color: "#E2154A",
			value: 42,
		},
	]

	const threatsBarChartData = [
		{
			label: "Economic Inequality",
			disagree: 15,
			agree: 64,
		},
		{
			label: "Limits on Free Speech",
			disagree: 25,
			agree: 53,
		},
		{
			label: "Unfair or Fraudulent Elections",
			disagree: 27,
			agree: 49,
		},
		{
			label: "Power of Big Tech",
			disagree: 23,
			agree: 48,
		},
		{
			label: "US’s Influence",
			disagree: 26,
			agree: 44,
		},
		{
			label: "Foreign Election Interference",
			disagree: 29,
			agree: 42,
		},
		{
			label: "China’s Influence",
			disagree: 32,
			agree: 38,
		},
		{
			label: "Russia’s Influence",
			disagree: 36,
			agree: 28,
		},
	]

	return (
		<>
			<SEO {...seo} />
			<Layout headerConfig={headerConfig} footerConfig={{ hideDemo: true, hideNewsletter: false }}>
				<ModuleRender sections={sections} />
				<VideoFormContainer>
					<Container>
						<VideoFormTop>
							<VideoFormLeft>
								<Title>What This Report Explores</Title>
								<P>
									{`The Democracy Perception Index (DPI) aims to understand the global crisis that democracy is facing from the perspective of ordinary people from around the world. It is the largest annual study on people’s perception of democracy, representing over 75% of the world’s population. 
                           
                           The Democracy Perception Index (DPI) was released ahead of the Alliance of Democracies Foundation’s fourth-annual Copenhagen Democracy Summit, held virtually with leaders and democracy activists from around the world.`}
								</P>
							</VideoFormLeft>
							<VideoFormRight>
								<RaportImage>
									<Media {...dpiRaportImage} />
								</RaportImage>
							</VideoFormRight>
						</VideoFormTop>
					</Container>
				</VideoFormContainer>
				<ChartsContainer>
					<Container>
						<ChartsTop>
							<MainTitle css={whiteText}>2021 Key Findings</MainTitle>
							<PieChartTitle css={whiteText}>Threats to Democracy</PieChartTitle>
							<DoubleBarChart
								title="Economic inequality is the biggest perceived threat to democracy"
								subTitle="% agree that ______ is a threat to democracy in their country"
								data={threatsBarChartData}
								whiteTheme
							/>
						</ChartsTop>
					</Container>
				</ChartsContainer>
				<ChartsContainer className="whiteBg">
					<Container>
						<ChartsTop>
							<MainTitle>Democracy During COVID</MainTitle>
							<ChartsSecondary>
								<Title2>COVID-19: Europe and America are failing in the eyes of their citizens</Title2>
								<SubTitle2>% say that their country is responding well to the COVID 19 crisis</SubTitle2>
							</ChartsSecondary>
							<Map>
								<Media {...dpiMapImage} />
							</Map>
							<SubMap>
								<Media {...dpiMapIndicator} />
							</SubMap>
						</ChartsTop>
						<ChartsBottom style={{ marginTop: "120px" }}>
							<ChartsLeft>
								<Title>People in Asia are most satisfied with their country’s response</Title>
								<P style={{ whiteSpace: "normal" }}>
									Average satisfaction in Asia is significantly higher than in Europe and Latin America. People living
									in free democracies are also much less satisfied with their country’s response, with only 51% saying
									their country is responding well.
								</P>
							</ChartsLeft>
							<ChartsRight>
								{(barCharts2 || []).map(chart => (
									<BarChart value={chart.value} colorHEX={chart.color} animate>
										{chart.label}
									</BarChart>
								))}
								<span>% say that their country is responding well to the COVID 19 crisis</span>
							</ChartsRight>
						</ChartsBottom>
					</Container>
				</ChartsContainer>
				<section css={footerCoverStyles}>
					<Container css={coverContainerStyles}>
						<H3 css={coverHeadingStyles}>
							Make your first step
							<br />
							the right one
						</H3>
						<P css={coverDescriptionStyles}>
							Learn how you can use Latana to improve <br />
							your brand marketing and grow faster.
						</P>
						<Button
							label="Book Demo"
							variant="primary"
							link={{
								slug: "book-demo",
							}}
							type="button"
						/>
					</Container>
				</section>
			</Layout>
		</>
	)
}

export default React.memo(DemocracyDownload)
